import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { WeightInput } from '../components/WeightInput.js'
import { api, ApiError } from '../api/client.js'

interface Customer {
  id: string
  name: string
  requires_quotation_acceptance: boolean
}
interface VehicleTypeOption {
  id: string
  name: string
}
interface Quotation {
  id: string
  quotation_no: string
  freight_rate: string | null
  status: string
  valid_till: string | null
}
interface Enquiry {
  id: string
  customer_id: string
  customer_name: string
  origin: string
  destination: string
  material: string | null
  weight: string | null
  status: string
  expected_pickup_date: string | null
  quotations?: Quotation[]
}

/** Stage 4 §2: Enquiry list -> per-row detail with the quotation lifecycle
 * (create/send/accept/reject/extend) inline, since a quotation only ever
 * exists in the context of the enquiry that produced it. */
export function EnquiriesPage() {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [enqRes, custRes, vtRes] = await Promise.all([
        api.get<{ rows: Enquiry[] }>('/enquiries?pageSize=50'),
        api.get<{ rows: Customer[] }>('/customers?pageSize=100'),
        // /vehicle-types is a master endpoint — {rows, total, ...}, not a bare
        // array — same shape as every other master list response.
        api
          .get<{ rows: { id: string; name: string }[] }>('/vehicle-types?pageSize=100')
          .catch(() => ({ rows: [] })),
      ])
      setEnquiries(enqRes.rows)
      setCustomers(custRes.rows)
      setVehicleTypes(vtRes.rows)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div>
      <div className="tf-toolbar">
        <h2 style={{ margin: 0 }}>Enquiries</h2>
        <button type="button" className="tf-button-primary" onClick={() => setFormOpen(true)}>
          + New Enquiry
        </button>
      </div>

      {error && <p className="tf-error">{error}</p>}

      <table className="tf-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Route</th>
            <th>Material</th>
            <th>Status</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={5}>Loading…</td>
            </tr>
          )}
          {!loading && enquiries.length === 0 && (
            <tr>
              <td colSpan={5}>No records found</td>
            </tr>
          )}
          {!loading &&
            enquiries.map((e) => (
              <tr key={e.id}>
                <td>{e.customer_name}</td>
                <td>
                  {e.origin} → {e.destination}
                </td>
                <td>{e.material ?? '—'}</td>
                <td>{e.status}</td>
                <td className="tf-row-actions">
                  <button type="button" onClick={() => setDetailId(e.id)}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {formOpen && (
        <NewEnquiryForm
          customers={customers}
          vehicleTypes={vehicleTypes}
          onClose={() => setFormOpen(false)}
          onCreated={() => {
            setFormOpen(false)
            void load()
          }}
        />
      )}

      {detailId && (
        <EnquiryDetail
          enquiryId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  )
}

function NewEnquiryForm({
  customers,
  vehicleTypes,
  onClose,
  onCreated,
}: {
  customers: Customer[]
  vehicleTypes: VehicleTypeOption[]
  onClose: () => void
  onCreated: () => void
}) {
  const [customerId, setCustomerId] = useState('')
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [material, setMaterial] = useState('')
  const [weight, setWeight] = useState('')
  const [vehicleTypeId, setVehicleTypeId] = useState('')
  const [pickupDate, setPickupDate] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      // branch_id comes from the caller's own session branch on the server
      // side for every other module; enquiries currently require it explicitly
      // (Stage 2 schema has no tenant-wide default), so it is looked up fresh.
      const branches = await api.get<{ id: string }[]>('/auth/branches')
      await api.post('/enquiries', {
        branch_id: branches[0]?.id,
        customer_id: customerId,
        origin,
        destination,
        material: material || undefined,
        weight: weight || undefined,
        vehicle_type_id: vehicleTypeId || undefined,
        expected_pickup_date: pickupDate || undefined,
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create enquiry')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>New Enquiry</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Customer</span>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required>
              <option value="" disabled>
                Select…
              </option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="tf-field">
            <span>Origin</span>
            <input value={origin} onChange={(e) => setOrigin(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Destination</span>
            <input value={destination} onChange={(e) => setDestination(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Material</span>
            <input value={material} onChange={(e) => setMaterial(e.target.value)} />
          </label>
          <WeightInput label="Weight" valueKg={weight} onChangeKg={setWeight} />
          <label className="tf-field">
            <span>Vehicle type needed</span>
            <select value={vehicleTypeId} onChange={(e) => setVehicleTypeId(e.target.value)}>
              <option value="">Any</option>
              {vehicleTypes.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          <label className="tf-field">
            <span>Expected pickup date</span>
            <input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} />
          </label>
          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EnquiryDetail({
  enquiryId,
  onClose,
  onChanged,
}: {
  enquiryId: string
  onClose: () => void
  onChanged: () => void
}) {
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rateInput, setRateInput] = useState('')
  const [validTillInput, setValidTillInput] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    try {
      const data = await api.get<Enquiry>(`/enquiries/${enquiryId}`)
      setEnquiry(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load enquiry')
    }
  }, [enquiryId])

  useEffect(() => {
    void reload()
  }, [reload])

  async function createQuotation(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(`/enquiries/${enquiryId}/quotation`, {
        freight_rate: rateInput || undefined,
        valid_till: validTillInput || undefined,
      })
      setRateInput('')
      setValidTillInput('')
      await reload()
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create quotation')
    } finally {
      setBusy(false)
    }
  }

  async function act(path: string, body?: unknown) {
    setBusy(true)
    setError(null)
    try {
      await api.post(path, body)
      await reload()
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  // Stage 4 §3.1: a booking is normally confirmed FROM an accepted quotation —
  // this is that action, kept here since the quotation only exists in this
  // context. Bookings for a skip-quotation customer are created directly from
  // the Bookings screen instead.
  async function convertToBooking(q: Quotation) {
    if (!enquiry) return
    setBusy(true)
    setError(null)
    try {
      const branches = await api.get<{ id: string }[]>('/auth/branches')
      await api.post('/bookings', {
        branch_id: branches[0]?.id,
        customer_id: enquiry.customer_id,
        quotation_id: q.id,
        enquiry_id: enquiry.id,
        origin: enquiry.origin,
        destination: enquiry.destination,
        material: enquiry.material ?? undefined,
        weight: enquiry.weight ?? undefined,
        freight_amount: q.freight_rate ?? undefined,
      })
      onChanged()
      // The enquiry is now Closed and has nothing further to do here — closing
      // the panel is the visible confirmation that the conversion succeeded,
      // rather than leaving it open showing a now-stale "Accepted" quotation
      // with a "Convert to Booking" button that would just error if clicked again.
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create booking')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Enquiry</h2>
        {!enquiry && <p className="tf-note">Loading…</p>}
        {enquiry && (
          <>
            <p className="tf-note">
              <strong>{enquiry.customer_name}</strong> · {enquiry.origin} → {enquiry.destination}
              <br />
              Status: {enquiry.status}
            </p>

            <h2>Quotations</h2>
            {(enquiry.quotations ?? []).map((q) => (
              <div key={q.id} className="tf-card" style={{ marginTop: '0.5rem' }}>
                <p className="tf-note">
                  <strong>{q.quotation_no}</strong> — ₹{q.freight_rate ?? '—'} — {q.status}
                  {q.valid_till && <> · valid till {q.valid_till.slice(0, 10)}</>}
                </p>
                <div className="tf-row-actions">
                  {/* Once the enquiry is Closed (booked, or explicitly lost) none of
                      its quotations can still be acted on — most importantly,
                      Convert to Booking must not be clickable a second time. */}
                  {enquiry.status !== 'Closed' && q.status === 'Draft' && (
                    <button type="button" disabled={busy} onClick={() => act(`/quotations/${q.id}/send`)}>
                      Send
                    </button>
                  )}
                  {enquiry.status !== 'Closed' && (q.status === 'Draft' || q.status === 'Sent') && (
                    <>
                      <button type="button" disabled={busy} onClick={() => act(`/quotations/${q.id}/accept`)}>
                        Accept
                      </button>
                      <button type="button" disabled={busy} onClick={() => act(`/quotations/${q.id}/reject`)}>
                        Reject
                      </button>
                    </>
                  )}
                  {enquiry.status !== 'Closed' && q.status === 'Accepted' && (
                    <button type="button" disabled={busy} onClick={() => convertToBooking(q)}>
                      Convert to Booking
                    </button>
                  )}
                  {enquiry.status !== 'Closed' && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        const newDate = window.prompt('Extend validity to (YYYY-MM-DD):')
                        if (newDate) void act(`/quotations/${q.id}/extend-validity`, { valid_till: newDate })
                      }}
                    >
                      Extend validity
                    </button>
                  )}
                </div>
              </div>
            ))}

            {enquiry.status !== 'Closed' && (
              <form onSubmit={createQuotation} className="tf-form" style={{ marginTop: '1rem' }}>
                <label className="tf-field">
                  <span>New quotation — freight rate (leave blank to use a matching rate contract)</span>
                  <input type="number" value={rateInput} onChange={(e) => setRateInput(e.target.value)} />
                </label>
                <label className="tf-field">
                  <span>Valid till</span>
                  <input type="date" value={validTillInput} onChange={(e) => setValidTillInput(e.target.value)} />
                </label>
                <button type="submit" className="tf-button-primary" disabled={busy}>
                  Create Quotation
                </button>
              </form>
            )}

            {error && <p className="tf-error">{error}</p>}
          </>
        )}
        <div className="tf-form-actions" style={{ marginTop: '1rem' }}>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
